/**
 * The shapes of the declared toolchain (spec 2026-10-08 §2): what a reader
 * asks for, and the plan a resolution produces. Kept apart from the resolver
 * so the environment schema and `qaToolchain` can name a plan without
 * importing the resolver that imports them.
 */
import { z } from "zod";
/** `pin` is an exact version file, `constraint` a range from a manifest, `hint` CI, a Dockerfile or a devcontainer. */
export const TOOL_REQUEST_STRENGTHS = ["pin", "constraint", "hint"];
/**
 * Why a requested tool is not on the plan. Each is a fact about the
 * repository's declaration, never an install failure: a refused backend, a
 * version under the install floor, a PHP line the image does not bake, a tool
 * that compiles for minutes, or an install whose lock carries no checksum.
 */
export const UNSATISFIABLE_REASONS = [
    "backend_not_allowed",
    "below_floor",
    "not_baked",
    "install_too_slow",
    "unverifiable",
];
/** How a `toolchain_install` step can fail; the build continues on what resolved. */
export const TOOLCHAIN_INSTALL_CAUSES = ["install_timed_out", "checksum_mismatch", "toolchain_install_failed"];
/** A mise tool name, optionally backend-prefixed: shell-safe by construction. */
export const TOOL_NAME_PATTERN = /^[a-z0-9][a-z0-9._-]*(?::[A-Za-z0-9._/@-]+)?$/;
/** A resolved version as it reaches a shell command: no spaces, quotes or operators. */
export const TOOL_VERSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._+-]*$/;
const toolName = z.string().max(120).regex(TOOL_NAME_PATTERN);
const toolVersion = z.string().max(80).regex(TOOL_VERSION_PATTERN);
const hostname = z.string().max(253).regex(/^[a-z0-9.-]+$/);
export const resolvedToolSchema = z
    .object({
    name: toolName,
    version: toolVersion,
    /** `core`, `aqua`, or a backend-prefixed package (`aqua:kubernetes/kubernetes/kubectl`). */
    backend: z.string().max(200),
    origin: z.enum(["baked", "install"]),
    /** The download hosts the install needs; empty for a baked tool. */
    hosts: z.array(hostname).max(20),
})
    .strict();
export const unsatisfiableToolSchema = z
    .object({
    tool: z.string().max(120),
    spec: z.string().max(200),
    reason: z.enum(UNSATISFIABLE_REASONS),
})
    .strict();
export const toolchainInstallFailureSchema = z
    .object({ tool: toolName, version: toolVersion, cause: z.enum(TOOLCHAIN_INSTALL_CAUSES) })
    .strict();
export const toolchainPlanSchema = z
    .object({
    tools: z.array(resolvedToolSchema).max(40),
    unsatisfiable: z.array(unsatisfiableToolSchema).max(40),
    /** Sentences for the record and the PR, e.g. a Terraform constraint OpenTofu satisfies. */
    notes: z.array(z.string().max(500)).max(20).optional(),
    /** Tools a setup run's install step could not install, with the step's cause. */
    installFailures: z.array(toolchainInstallFailureSchema).max(40).optional(),
    /** The agent PATH, for the home the plan was resolved for. */
    path: z.string().max(4000),
    env: z.record(z.string().max(100), z.string().max(4000)),
})
    .strict();
//# sourceMappingURL=toolchainPlanTypes.js.map